'use client';

import { genericMemo } from '@/types/use';
import {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import {
    MultiSelect as MultiSelectBySersavan,
    MultiSelectGroup,
    MultiSelectOption,
    MultiSelectProps,
} from './multi-select';

export interface MultiSelectCustomProps<T = any>
    extends Omit<MultiSelectProps, 'onChange' | 'onValueChange' | 'value'> {
    value?: string[];
    onChange?: (value: string[], options?: T[]) => void;
}

export interface MultiSelectCustomRefs<T = any> {}

const MultiSelectCustom = forwardRef<
    MultiSelectCustomRefs,
    MultiSelectCustomProps
>(function MultiSelectCustom<T = any>(
    props: MultiSelectCustomProps<T>,
    ref: ForwardedRef<MultiSelectCustomRefs<T>>
) {
    const { value = [], onChange, options, onSearch, ...passingProps } = props;

    const [realOptions, setRealOptions] = useState<
        MultiSelectOption<T>[] | MultiSelectGroup<T>[]
    >([]);

    const onCollectSaveOptions = useCallback(
        (opts: MultiSelectOption<T>[] | MultiSelectGroup<T>[]) => {
            setRealOptions((prev) => {
                const holdOptions = findCorrentOptions(value, prev);
                return removeDuplicateOptions([
                    ...holdOptions,
                    ...opts,
                ] as any) as any;
            });
        },
        [value]
    );

    const handleChange = useCallback(
        (value: string[]) => {
            let options: T[] = realOptions
                .filter((item) => {
                    if ('value' in item && value.includes(item.value)) {
                        return true;
                    } else return false;
                })
                .map((x) => (x as any)?._origin!) as T[];

            onChange?.(value, options);
        },
        [onChange, realOptions]
    );

    const _refOneTime = useRef<boolean>(false);

    useEffect(() => {
        if (passingProps.isLoading) {
            _refOneTime.current = false;
        }
    }, [passingProps.isLoading]);

    useEffect(() => {
        if (options.length === 0 || _refOneTime.current) return;
        onCollectSaveOptions(options);
        _refOneTime.current = true;
    }, [onCollectSaveOptions, options]);

    useImperativeHandle(ref, () => ({}), []);
    // TODO: vẫn còn lỗi
    return (
        <div className={passingProps.className}>
            <MultiSelectBySersavan
                {...passingProps}
                onSearch={
                    typeof onSearch === 'function'
                        ? (keyword: string) => {
                              _refOneTime.current = false;
                              onSearch(keyword);
                          }
                        : undefined
                }
                options={realOptions}
                value={value}
                defaultValue={value}
                onValueChange={handleChange}
            />
        </div>
    );
});

function findCorrentOptions(
    ids: string[],
    options: MultiSelectGroup[] | MultiSelectOption[]
): MultiSelectGroup[] | MultiSelectOption[] {
    if (ids.length === 0) {
        return options;
    }

    let result: MultiSelectGroup[] | MultiSelectOption[] = [];
    options.forEach((item) => {
        if ('options' in item) {
            item.options.forEach((subItem) => {
                if (ids.includes(subItem.value)) {
                    result.push(item as any);
                }
            });
        } else {
            if (ids.includes(item.value)) {
                result.push(item as any);
            }
        }
    });
    return result;
}

function removeDuplicateOptions(
    options: MultiSelectOption[] | MultiSelectGroup[]
) {
    const map = new Map<string, MultiSelectOption | MultiSelectGroup>();
    options.forEach((item) => {
        let key;

        if ('value' in item) {
            key = item.value;
        } else if ('heading' in item) {
            key = item.heading;
        }

        if (!!key) {
            map.set(key, item);
        }
    });

    const returnValue = Array.from(map.values());

    return returnValue;
}

export default genericMemo(MultiSelectCustom);
export type * from './multi-select';
